import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { userApi } from '../config';
import { addMonths, addYears, subDays } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  Video,
  ArrowLeft,
} from 'lucide-react';

const BookingStep2: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const classId = searchParams.get('classId');
  const plan = searchParams.get('plan') || 'Weekly';
  const amt = searchParams.get('amt') || '₹1,499';
  const mode = searchParams.get('mode') || 'Online';
  const selectedDateParam = searchParams.get('date') || '';
  const selectedTimeParam = searchParams.get('time') || '';

  const [classDetails, setClassDetails] = useState<any>(null);
  const [instructor, setInstructor] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [availableDates, setAvailableDates] = useState<{ date: Date; name: string; num: number; avail: string }[]>([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [datePageStart, setDatePageStart] = useState(0);
  const [selectedSlots, setSelectedSlots] = useState<{ dayIndex: number; slotIndex: number }[]>([]);

  const formatTime12Hour = (time?: string) => {
    if (!time) return 'TBD';
    const cleaned = String(time).replace(/undefined/gi, '').replace(/\s+/g, ' ').replace(/\s+,/g, ',').trim();
    const match = cleaned.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!match) return cleaned;

    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = (match[3] || (hours >= 12 ? 'PM' : 'AM')).toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const displayHours = hours % 12 || 12;
    const displayPeriod = hours >= 12 ? 'PM' : 'AM';
    return `${String(displayHours).padStart(2, '0')}:${minutes} ${displayPeriod}`;
  };

  const parseSlotRange = (slotValue: string) => {
    const [start = '', end = ''] = String(slotValue || '').split('-').map((part) => part.trim());
    return { start, end };
  };

  const getLocalDateKey = (value: Date | string | number) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeRangeLabel = (value: string) => {
    const { start, end } = parseSlotRange(value);
    if (!start && !end) return formatTime12Hour(value).trim().toUpperCase();
    return `${formatTime12Hour(start)} - ${formatTime12Hour(end)}`.trim().toUpperCase();
  };

  const toMinutes = (value: string) => {
    const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = (match[3] || '').toUpperCase();
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const slots =
    classDetails?.googleMeetLinks
      ?.map((link: any) => {
        const startTime = formatTime12Hour(link.startTime || '');
        const endTime = formatTime12Hour(link.endTime || '');
        return {
          id: link.id || link.slotId || '',
          displayTime: link.displayTime || link.time || `${startTime} - ${endTime}`,
          startTime,
          endTime,
          rawTime: link.time || `${startTime} - ${endTime}`,
        };
      })
      .filter((link: any) => link.displayTime) || [];

  const bookedSlots = Array.isArray(classDetails?.bookedSlots) ? classDetails.bookedSlots : [];
  const isRecurringClass = classDetails?.classType === 'recurring';
  const isFixedClass = classDetails?.classType === 'fixed';
  const planKey = plan.toLowerCase();
  const isMonthlyPlan = planKey.includes('monthly');
  const isYearlyPlan = planKey.includes('yearly');
  const isDateOnlyPlan = isRecurringClass || isMonthlyPlan || isYearlyPlan;
  const hasSlots = slots.length > 0 && !isDateOnlyPlan;
  const isSingleStartDatePlan = isMonthlyPlan || isYearlyPlan;

  const formatDateLabel = (date: Date) =>
    date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const getPlanCoverageEnd = (start: Date) => {
    if (isMonthlyPlan) return subDays(addMonths(start, 1), 1);
    if (isYearlyPlan) return subDays(addYears(start, 1), 1);
    if (classDetails?.classEndDate) return new Date(classDetails.classEndDate);
    return addYears(start, 1);
  };

  const getSelectedCoverageStart = () => {
    const selectedDate = availableDates[selectedDayIndex]?.date;
    if (selectedDate && !Number.isNaN(selectedDate.getTime())) {
      return selectedDate;
    }

    if (classDetails?.classStartDate) {
      const fallbackDate = new Date(classDetails.classStartDate);
      if (!Number.isNaN(fallbackDate.getTime())) {
        return fallbackDate;
      }
    }

    return null;
  };

  useEffect(() => {
    if (classId) {
      const fetchDetails = async () => {
        try {
          const res: any = await userApi.get(`/fetch-class-details?classId=${classId}`);
          if (res.data?.data) {
            setClassDetails(res.data.data.class);
            setInstructor(res.data.data.instructor);
          }
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    } else {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (!classDetails) return;

    const dayNamesArr = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const datesList: { date: Date; name: string; num: number; avail: string }[] = [];
    const startDate = classDetails.classStartDate ? new Date(classDetails.classStartDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const generationStart =
      startDate && !Number.isNaN(startDate.getTime()) && startDate > today
        ? new Date(startDate)
        : new Date(today);
    const hasScheduleDays = classDetails.schedule && Object.keys(classDetails.schedule).length > 0;
    const effectiveEndDate = startDate ? getPlanCoverageEnd(generationStart) : addYears(generationStart, 1);
    const isFixedRange =
      classDetails.classType === 'fixed' &&
      startDate &&
      effectiveEndDate &&
      !Number.isNaN(startDate.getTime()) &&
      !Number.isNaN(effectiveEndDate.getTime());

    if (hasScheduleDays) {
      const activeDaysSet = new Set(Object.keys(classDetails.schedule).map((d) => d.toLowerCase()));
      const cursor = new Date(generationStart);
      let safety = 0;
      while ((!effectiveEndDate || cursor <= effectiveEndDate) && safety < 400) {
        const d = new Date(cursor);
        const dayName = dayNamesArr[d.getDay()];
        if (activeDaysSet.has(dayName)) {
          datesList.push({
            date: d,
            name: dayName.substring(0, 3).toUpperCase(),
            num: d.getDate(),
            avail: 'Open',
          });
        }
        cursor.setDate(cursor.getDate() + 1);
        safety++;
      }
    } else if (isFixedRange && startDate && effectiveEndDate) {
      const cursor = new Date(generationStart);
      let safety = 0;
      while (cursor <= effectiveEndDate && safety < 400) {
        datesList.push({
          date: new Date(cursor),
          name: dayNamesArr[cursor.getDay()].substring(0, 3).toUpperCase(),
          num: cursor.getDate(),
          avail: 'Open',
        });
        cursor.setDate(cursor.getDate() + 1);
        safety++;
      }
    }

    setAvailableDates(datesList);

    const requestedDateKey = selectedDateParam ? selectedDateParam.slice(0, 10) : '';
    const requestedIndex = requestedDateKey
      ? datesList.findIndex((d) => getLocalDateKey(d.date) === requestedDateKey)
      : -1;
    const firstUpcomingIndex = datesList.findIndex((d) => {
      const copy = new Date(d.date);
      copy.setHours(0, 0, 0, 0);
      return copy >= today;
    });
    const nextIndex = requestedIndex >= 0 ? requestedIndex : firstUpcomingIndex >= 0 ? firstUpcomingIndex : 0;
    if (datesList.length > 0) {
      setSelectedDayIndex(nextIndex);
      setDatePageStart(Math.max(0, Math.min(nextIndex - 2, Math.max(datesList.length - 7, 0))));
      if (slots.length > 0 && !isDateOnlyPlan) {
        const requestedSlotIndex = selectedTimeParam
          ? slots.findIndex((slot: any) => {
              const normalizedQuery = normalizeRangeLabel(selectedTimeParam);
              return (
                normalizedQuery === normalizeRangeLabel(slot.displayTime) ||
                normalizedQuery === normalizeRangeLabel(slot.rawTime)
              );
            })
          : -1;
        setSelectedSlots([{ dayIndex: nextIndex, slotIndex: requestedSlotIndex >= 0 ? requestedSlotIndex : 0 }]);
      } else {
        setSelectedSlots([]);
      }
    }
  }, [classDetails, slots.length, selectedDateParam, selectedTimeParam, isDateOnlyPlan]);

  const getDayStr = (dIdx: number) => {
    const dInfo = availableDates[dIdx];
    if (dInfo) {
      return `${dInfo.name.charAt(0)}${dInfo.name.slice(1).toLowerCase()}, ${dInfo.num} ${dInfo.date.toLocaleString('default', { month: 'short' })}`;
    }

    if (isFixedClass && classDetails?.classStartDate) {
      const fixedDate = new Date(classDetails.classStartDate);
      if (!Number.isNaN(fixedDate.getTime())) {
        return fixedDate.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      }
    }

    return 'TBD';
  };

  const getDateKey = (dIdx: number) => {
    const dInfo = availableDates[dIdx];
    if (dInfo) {
      return getLocalDateKey(dInfo.date);
    }

    if (isFixedClass && classDetails?.classStartDate) {
      const fixedDate = new Date(classDetails.classStartDate);
      if (!Number.isNaN(fixedDate.getTime())) {
        return getLocalDateKey(fixedDate);
      }
    }

    return '';
  };

  const coverageLabel = (() => {
    const start = getSelectedCoverageStart();
    if (!start) return '';
    const end = getPlanCoverageEnd(start);
    if (Number.isNaN(end.getTime())) return '';

    if (isMonthlyPlan) return `Monthly coverage: ${formatDateLabel(start)} - ${formatDateLabel(end)}`;
    if (isYearlyPlan) return `Yearly coverage: ${formatDateLabel(start)} - ${formatDateLabel(end)}`;
    if (classDetails?.classEndDate) return `Dates: ${formatDateLabel(start)} - ${formatDateLabel(end)}`;
    return `Dates: ${formatDateLabel(start)} onwards`;
  })();

  const visibleDates = availableDates.slice(datePageStart, datePageStart + 7);
  const canGoPrev = datePageStart > 0;
  const canGoNext = datePageStart + 7 < availableDates.length;

  const isSlotBooked = (dayIndex: number, slotIndex: number) => {
    const slotLabel = slots[slotIndex]?.displayTime || '';
    const dateKey = getDateKey(dayIndex);
    return bookedSlots.some((entry: any) => {
      const entryDate = String(entry?.date || entry?.bookingDate || '').slice(0, 10);
      if (entryDate !== dateKey) return false;

      const entrySlotId = String(entry?.slotId || entry?.slot?.slotId || '').trim();
      const currentSlotId = String(
        classDetails?.googleMeetLinks?.[slotIndex]?.id ||
          classDetails?.googleMeetLinks?.[slotIndex]?.slotId ||
          ''
      ).trim();
      if (entrySlotId && currentSlotId && entrySlotId === currentSlotId) {
        return true;
      }

      const rawEntryTime =
        entry?.time ||
        entry?.preferredTimeSlot ||
        (entry?.slot?.startTime && entry?.slot?.endTime
          ? `${entry.slot.startTime} - ${entry.slot.endTime}`
          : `${entry?.startTime || entry?.slot?.startTime || ''} - ${entry?.endTime || entry?.slot?.endTime || ''}`);
      const entryTime = formatTime12Hour(rawEntryTime);

      return entryTime === slotLabel;
    });
  };

  const isSlotPast = (dayIndex: number, slotIndex: number) => {
    const dateKey = getDateKey(dayIndex);
    const todayKey = getLocalDateKey(new Date());
    if (dateKey !== todayKey) return false;

    const endTime = slots[slotIndex]?.endTime || '';
    const endMinutes = toMinutes(endTime);
    if (endMinutes === null) return false;

    const now = new Date();
    return now.getHours() * 60 + now.getMinutes() >= endMinutes;
  };

  const numericAmt = parseInt(amt.replace(/[^\d]/g, ''), 10) || 0;
  const currencySymbol = amt.replace(/[\d,.]/g, '').trim() || '₹';
  const multiplier = Math.max(1, selectedSlots.length);
  const totalAmt = (numericAmt * multiplier).toLocaleString('en-IN');
  const finalAmtString = numericAmt > 0 ? `${currencySymbol}${totalAmt}` : amt;

  const handleDateSelect = (index: number) => {
    setSelectedDayIndex(index);
    setSelectedSlots([]);
  };

  const scrollDates = (direction: -1 | 1) => {
    setDatePageStart((prev) => {
      const next = prev + direction * 7;
      return Math.max(0, Math.min(next, Math.max(availableDates.length - 7, 0)));
    });
  };

  const handleDateInput = (value: string) => {
    const nextIndex = availableDates.findIndex((item) => getLocalDateKey(item.date) === value);
    if (nextIndex >= 0) {
      setDatePageStart(Math.max(0, Math.min(nextIndex - 2, Math.max(availableDates.length - 7, 0))));
      handleDateSelect(nextIndex);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-[#15302B]/20 border-t-[#15302B] rounded-full animate-spin" />
          <p className="text-xs font-semibold text-[#15302B]">Loading booking details...</p>
        </div>
      </div>
    );
  }

  const currentMonthYear =
    availableDates[selectedDayIndex]?.date
      ? availableDates[selectedDayIndex].date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
      : 'Session Date';

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF9F6] text-[#1E293B] antialiased py-3 sm:py-5 px-3 sm:px-6 lg:px-8 pb-24">
      <div className="max-w-4xl mx-auto space-y-2.5 sm:space-y-3">
        {/* Top Back Navigation Arrow */}
        <div className="pt-0.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center text-[#E05638] hover:text-[#C54528] transition-colors cursor-pointer"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 stroke-[2.5]" />
          </button>
        </div>

        {/* 1. Summary Header Card (Matches luxury retreat book page) */}
        <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-bold text-[#1E293B] leading-tight">
              {classDetails?.classTitle || 'Wellness Session'}
            </h1>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5 flex flex-wrap items-center gap-1.5">
              <span>{instructor?.name || (slug ? slug.replace(/-/g, ' ') : 'Wellness Mentor')}</span>
              <span>•</span>
              <span className="capitalize">{mode}</span>
              {classDetails?.classDuration && (
                <>
                  <span>•</span>
                  <span>{classDetails.classDuration}</span>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-col sm:items-end shrink-0 pt-1.5 sm:pt-0 border-t sm:border-t-0 border-slate-100">
            <span className="text-[9px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-none mb-0.5">
              Plan Price
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm sm:text-base font-bold text-[#E05638] leading-tight">
                {finalAmtString}
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium capitalize">
                / {plan}
              </span>
            </div>
          </div>
        </div>

        {/* Plan Coverage Card (if available) */}
        {coverageLabel && (
          <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2 sm:px-4 sm:py-2.5 shadow-2xs flex items-center gap-2">
            <span className="text-[10px] font-bold text-[#E05638] uppercase tracking-wider bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-md">
              Coverage
            </span>
            <span className="text-xs sm:text-[13px] font-medium text-slate-800">
              {coverageLabel}
            </span>
          </div>
        )}

        {/* Offline Venue Details (if offline) */}
        {mode.toLowerCase() === 'offline' &&
          (classDetails?.venueAddress || classDetails?.venueLocation || classDetails?.venuePhoto) && (
            <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xs space-y-2">
              <div className="flex items-center gap-1.5">
                <span className="w-1 h-3.5 bg-[#E05638] rounded-full inline-block" />
                <h2 className="font-bold text-xs sm:text-[13px] text-[#1E293B]">Venue Details</h2>
              </div>
              <div className="flex gap-3 flex-wrap sm:flex-nowrap items-start text-xs text-slate-700">
                {classDetails.venuePhoto && (
                  <img
                    src={classDetails.venuePhoto}
                    alt="Venue"
                    className="w-20 h-16 rounded-lg object-cover border border-slate-200 shrink-0"
                  />
                )}
                <div className="flex-1">
                  {classDetails.venueAddress && (
                    <p className="font-medium text-slate-900 mb-1">{classDetails.venueAddress}</p>
                  )}
                  {classDetails.venueLocation && (
                    <a
                      href={classDetails.venueLocation}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#E05638] font-semibold inline-flex items-center gap-1 hover:underline"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      <span>View on Google Maps</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* 2. Select Date Section (Matches retreat book page) */}
        {availableDates.length > 0 && (
          <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xs space-y-2.5">
            {/* Header with Orange Accent Bar & Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-1 h-3.5 bg-[#E05638] rounded-full inline-block" />
                <h2 className="font-bold text-xs sm:text-[13px] text-[#1E293B]">Select Date</h2>
                <span className="text-[11px] text-slate-500 font-medium ml-1">({currentMonthYear})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={getDateKey(selectedDayIndex)}
                  min={getLocalDateKey(new Date())}
                  onChange={(e) => handleDateInput(e.target.value)}
                  className="text-[10px] sm:text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5 text-slate-700 bg-white"
                />
                <button
                  type="button"
                  onClick={() => scrollDates(-1)}
                  disabled={!canGoPrev}
                  className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-20 cursor-pointer transition-colors"
                  aria-label="Previous dates"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollDates(1)}
                  disabled={!canGoNext}
                  className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-20 cursor-pointer transition-colors"
                  aria-label="Next dates"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Quick Dates Tiles Grid (Matches luxury retreat book page) */}
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5 sm:gap-2 pt-0.5">
              {visibleDates.map((item, index) => {
                const realIndex = datePageStart + index;
                const isSelected = selectedDayIndex === realIndex;
                const hasSelectedSlotOnThisDay = selectedSlots.some((sel) => sel.dayIndex === realIndex);
                const monthShort = item.date.toLocaleString('default', { month: 'short' });

                return (
                  <button
                    key={realIndex}
                    type="button"
                    onClick={() => handleDateSelect(realIndex)}
                    className={`flex flex-col items-center justify-center rounded-lg border py-1.5 sm:py-2 px-1 w-full transition-all cursor-pointer select-none ${
                      isSelected
                        ? 'bg-[#0F172A] text-white border-[#0F172A] shadow-xs'
                        : hasSelectedSlotOnThisDay
                          ? 'bg-emerald-50 text-emerald-950 border-emerald-300'
                          : 'bg-white text-[#334155] border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`text-[9px] sm:text-[10px] font-semibold tracking-wider ${
                        isSelected ? 'text-slate-300' : 'text-[#64748B]'
                      }`}
                    >
                      {item.name}
                    </span>
                    <span className="text-sm sm:text-base font-bold my-0.5 leading-none">{item.num}</span>
                    <span
                      className={`text-[9px] sm:text-[10px] font-medium ${
                        isSelected ? 'text-slate-300' : 'text-[#64748B]'
                      }`}
                    >
                      {monthShort}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. Select Time Slots Section (Matches luxury retreat book page) */}
        {hasSlots && (
          <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xs space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-1 h-3.5 bg-[#E05638] rounded-full inline-block" />
                <h2 className="font-bold text-xs sm:text-[13px] text-[#1E293B]">Select Time Slot</h2>
                <span className="text-[11px] text-slate-500 font-medium ml-1">
                  ({getDayStr(selectedDayIndex)})
                </span>
              </div>
              {selectedSlots.length > 0 && (
                <span className="text-[10.5px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                  {selectedSlots.length} slot selected
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-0.5">
              {slots.map((slot: any, i: number) => {
                const isBooked = isSlotBooked(selectedDayIndex, i);
                const isPast = isSlotPast(selectedDayIndex, i);
                const isDisabled = isBooked || isPast;
                const isSelected = selectedSlots.some(
                  (sel) => sel.dayIndex === selectedDayIndex && sel.slotIndex === i
                );

                return (
                  <button
                    key={i}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      setSelectedSlots((prev) => {
                        const exists = prev.some(
                          (sel) => sel.dayIndex === selectedDayIndex && sel.slotIndex === i
                        );
                        return exists ? [] : [{ dayIndex: selectedDayIndex, slotIndex: i }];
                      });
                    }}
                    className={`relative flex items-center justify-center py-2.5 px-2 rounded-xl border text-xs font-semibold transition-all select-none ${
                      isSelected
                        ? 'bg-[#0F172A] text-white border-[#0F172A] shadow-xs cursor-pointer'
                        : isDisabled
                          ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                          : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300 hover:bg-slate-50 cursor-pointer'
                    }`}
                  >
                    <span className={isDisabled ? 'line-through' : ''}>{slot.displayTime}</span>
                    {isBooked && (
                      <span className="absolute -top-1.5 -right-1 text-[8px] font-bold px-1.5 py-0.2 rounded-full bg-amber-700 text-white shadow-2xs">
                        SOLD OUT
                      </span>
                    )}
                    {isPast && !isBooked && (
                      <span className="absolute -top-1.5 -right-1 text-[8px] font-bold px-1.5 py-0.2 rounded-full bg-slate-400 text-white shadow-2xs">
                        CLOSED
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 4. Fixed Bottom Total & Next Navigation Bar (Matches luxury retreat book page) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 py-2.5 sm:py-3 px-4 sm:px-8 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
          <div>
            <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider leading-none mb-0.5">
              {plan} • {mode.toUpperCase()}
            </p>
            <p className="text-base sm:text-lg font-bold text-[#0F172A] leading-tight">
              {finalAmtString}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              disabled={!isDateOnlyPlan && hasSlots && selectedSlots.length === 0}
              onClick={() => {
                const query = new URLSearchParams(location.search);
                if (!isDateOnlyPlan && selectedSlots.length > 0) {
                  query.set('date', getDateKey(selectedSlots[0].dayIndex));
                } else if (availableDates.length > 0) {
                  query.set('date', getDateKey(selectedDayIndex));
                } else if (isFixedClass && classDetails?.classStartDate) {
                  query.set('date', getDateKey(selectedDayIndex));
                }
                if (!isDateOnlyPlan && hasSlots && selectedSlots.length > 0) {
                  query.set(
                    'time',
                    slots[selectedSlots[0].slotIndex]?.displayTime ||
                      slots[selectedSlots[0].slotIndex]?.rawTime ||
                      ''
                  );
                }
                query.set('totalAmt', numericAmt > 0 ? (numericAmt * multiplier).toString() : amt);
                navigate(`/booking/${slug || 'instructor'}/step3?${query.toString()}`, {
                  state: location.state,
                });
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F172A] hover:bg-neutral-800 text-white px-6 py-2.5 text-xs sm:text-sm font-bold shadow-xs transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <span>Continue to checkout</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingStep2;
